import React from 'react';
import { Navigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

type Role = 'ADMIN' | 'SALES_MANAGER' | 'SALES_PERSON' | 'MENTOR' | 'STORE' | 'USER';

/**
 * نقش‌های مؤثر کاربر: نقش اصلی به‌علاوهٔ نقش‌های افزوده.
 * همتای همین تابع در سمت سرور، لایهٔ اصلیِ کنترل دسترسی است.
 */
export const effectiveRoles = (user?: { role?: string; extraRoles?: string[] | null }): string[] =>
  user ? Array.from(new Set([user.role, ...(user.extraRoles ?? [])].filter(Boolean) as string[])) : [];

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * نقش‌های مجاز. اگر ندهید، فقط ورود بررسی می‌شود.
   * این لایهٔ دوم است — لایهٔ اول گاردهای @Roles روی خود اندپوینت‌هاست.
   */
  roles?: Role[];
}

/** صفحهٔ اول هر نقش، وقتی به مسیری می‌رود که اجازه‌اش را ندارد */
export const homeFor = (role?: string) => {
  if (role === 'MENTOR') return '/portal-mentor';
  if (role === 'STORE') return '/assessments';
  if (role === 'SALES_PERSON') return '/my-workshops';
  return '/dashboard';
};

const Forbidden: React.FC<{ role?: string }> = ({ role }) => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center" dir="rtl">
    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
      <ShieldAlert className="h-6 w-6" />
    </div>
    <p className="text-base font-bold text-gray-900">به این بخش دسترسی ندارید</p>
    <p className="mt-1 max-w-sm text-sm leading-7 text-gray-500">
      این صفحه برای نقش شما در دسترس نیست. اگر فکر می‌کنید اشتباهی رخ داده، با مدیریت دوره
      تماس بگیرید.
    </p>
    <a
      href={homeFor(role)}
      className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700"
    >
      بازگشت به صفحهٔ خودم
    </a>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const owned = effectiveRoles(user);
  if (roles && !roles.some((role) => owned.includes(role))) {
    return <Forbidden role={user.role} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
